import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LogOut,
  Crown,
  Loader2,
  Calendar as CalendarIcon,
  Mail,
  BookOpen,
  CreditCard,
  Image as ImageIcon,
  Activity,
  MessageSquare,
  Users,
  Share2,
} from "lucide-react";
import { me, logout, adminGet, adminPost } from "@/lib/auth";
import { toast } from "sonner";

const ORDER_NOTIFY_TEMPLATES = [
  { id: "questionnaire_reminder", label: "Questionnaire Reminder" },
  { id: "first_proof_ready", label: "First Proof Ready" },
  { id: "revision_update", label: "Revision Update" },
  { id: "remaining_payment_required", label: "Remaining Payment Due" },
  { id: "production_started", label: "Production Started" },
  { id: "order_shipped", label: "Order Shipped" },
];

const TABS = [
  { id: "overview", label: "Overview", icon: Crown },
  { id: "bookings", label: "Bookings", icon: CalendarIcon },
  { id: "inquiries", label: "Inquiries", icon: MessageSquare },
  { id: "ebook-leads", label: "Ebook Leads", icon: BookOpen },
  { id: "photo-submissions", label: "Submissions", icon: ImageIcon },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "referrals", label: "Legacy Members", icon: Users },
  { id: "referral-events", label: "Referral Events", icon: Share2 },
  { id: "analytics-events", label: "Analytics", icon: Activity },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    me()
      .then((u) => { setUser(u); setLoading(false); })
      .catch(() => navigate("/admin/login", { replace: true }));
  }, [navigate]);

  useEffect(() => {
    if (!user || tab === "overview") return;
    setData(null);
    adminGet(tab)
      .then(setData)
      .catch(() => toast.error("Failed to load data."));
  }, [user, tab]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Crown className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="font-serif-display text-base md:text-lg">
              Crowned Canvas <span className="text-[#D4AF37]">Studio</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden md:inline overline text-white/50">{user?.email}</span>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-white/15 text-white/80 px-4 py-2 text-[10px] uppercase tracking-[0.22em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-10 grid lg:grid-cols-12 gap-8">
        {/* Tabs */}
        <aside className="lg:col-span-3 lg:sticky lg:top-24 self-start">
          <p className="overline mb-4">Studio Pulse</p>
          <nav className="grid gap-1">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  data-testid={`admin-tab-${t.id}`}
                  className={`group flex items-center justify-between gap-3 px-4 py-3 border transition-colors text-left ${
                    active
                      ? "border-[#D4AF37]/60 bg-[#D4AF37]/[0.06] text-[#D4AF37]"
                      : "border-white/10 text-white/70 hover:border-white/30 hover:text-white"
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <Icon className="w-4 h-4" />
                    <span className="text-xs uppercase tracking-[0.22em]">{t.label}</span>
                  </span>
                </button>
              );
            })}
          </nav>
        </aside>

        <section className="lg:col-span-9">
          {tab === "overview" ? <Overview /> : <DataTable type={tab} data={data} />}
        </section>
      </div>
    </div>
  );
}

function Overview() {
  const [dash, setDash] = useState(null);
  useEffect(() => {
    adminGet("dashboard").then(setDash).catch(() => toast.error("Failed to load dashboard."));
  }, []);

  if (!dash) {
    return (
      <div className="py-20 flex justify-center"><Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" /></div>
    );
  }
  const counts = dash.counts || {};
  return (
    <div className="space-y-10">
      <div>
        <p className="overline">At A Glance</p>
        <h1 className="font-serif-display text-3xl md:text-5xl mt-3 leading-tight">
          The studio, <span className="italic text-[#D4AF37]">today.</span>
        </h1>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
        <StatCard label="Bookings" value={counts.bookings} />
        <StatCard label="Ebook Leads" value={counts.ebook_leads} />
        <StatCard label="Inquiries" value={counts.inquiries} />
        <StatCard label="Photo Submissions" value={counts.photo_submissions} />
        <StatCard label="Payments Started" value={counts.payment_transactions} />
        <StatCard label="Payments Paid" value={counts.payments_paid} highlight />
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <RecentList title="Recent Bookings" rows={dash.recent?.bookings || []} renderRow={(r) => (
          <>
            <div className="text-white">{r.name}</div>
            <div className="text-white/50 text-xs">{r.preferred_date} — {r.preferred_time}</div>
          </>
        )} />
        <RecentList title="Recent Ebook Leads" rows={dash.recent?.ebook_leads || []} renderRow={(r) => (
          <>
            <div className="text-white">{r.email}</div>
            <div className="text-white/50 text-xs">{r.source}</div>
          </>
        )} />
      </div>
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div className={`bg-[#111111] p-7 ${highlight ? "border-l-2 border-[#D4AF37]" : ""}`}>
      <p className="overline text-white/55">{label}</p>
      <p className={`font-serif-display text-4xl mt-3 ${highlight ? "text-[#D4AF37]" : "text-white"}`}>
        {value ?? 0}
      </p>
    </div>
  );
}

function RecentList({ title, rows, renderRow }) {
  return (
    <div className="border border-white/10 p-6">
      <p className="overline">{title}</p>
      {rows.length === 0 ? (
        <p className="text-white/40 text-sm mt-4 font-light">No data yet.</p>
      ) : (
        <ul className="mt-4 divide-y divide-white/5">
          {rows.map((r, i) => (
            <li key={i} className="py-3 text-sm">{renderRow(r)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DataTable({ type, data }) {
  if (!data) {
    return <div className="py-20 flex justify-center"><Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" /></div>;
  }
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="border border-white/10 p-10 text-center">
        <p className="overline text-white/50">No Records</p>
        <p className="font-serif-display text-2xl mt-4">Nothing here yet.</p>
      </div>
    );
  }
  // Decide columns by type
  const columns = pickColumns(type, data[0]);
  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="font-serif-display text-2xl md:text-3xl">{labelForTab(type)}</h1>
        <p className="overline">{data.length} record{data.length === 1 ? "" : "s"}</p>
      </div>
      <div className="overflow-x-auto border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03]">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className="text-left px-4 py-3 overline text-white/60 whitespace-nowrap">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={i} className="border-t border-white/5 hover:bg-white/[0.02] transition-colors">
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-3 align-top text-white/85 max-w-[260px]">
                    {c.render ? c.render(row[c.key], row) : formatCell(row[c.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function labelForTab(t) {
  return TABS.find((x) => x.id === t)?.label || t;
}

function formatCell(v) {
  if (v == null || v === "") return <span className="text-white/30">—</span>;
  if (typeof v === "object") return <code className="text-xs text-white/60">{JSON.stringify(v)}</code>;
  if (typeof v === "string" && v.length > 80) return <span title={v}>{v.slice(0, 80)}…</span>;
  return String(v);
}

function pickColumns(type, sample) {
  const date = { key: "created_at", label: "Date", render: (v) => v ? new Date(v).toLocaleString() : "—" };
  switch (type) {
    case "bookings":
      return [
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "package_id", label: "Package" },
        { key: "preferred_date", label: "Date" },
        { key: "preferred_time", label: "Time" },
        { key: "status", label: "Status" },
        date,
      ];
    case "inquiries":
      return [
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "message", label: "Message" },
        date,
      ];
    case "ebook-leads":
      return [
        { key: "email", label: "Email" },
        { key: "name", label: "Name" },
        { key: "source", label: "Source" },
        date,
      ];
    case "photo-submissions":
      return [
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "occasion", label: "Occasion" },
        { key: "file_paths", label: "Photos", render: (paths) => (
            Array.isArray(paths) && paths.length > 0 ? (
              <div className="flex gap-1 flex-wrap">
                {paths.map((p) => (
                  <a key={p} href={`${process.env.REACT_APP_BACKEND_URL}${p}`} target="_blank" rel="noreferrer" className="w-10 h-10 border border-white/10 overflow-hidden hover:border-[#D4AF37]">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${p}`} alt="" className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            ) : "—"
          ) },
        { key: "status", label: "Status" },
        date,
      ];
    case "payments":
      return [
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "product_name", label: "Product" },
        { key: "amount", label: "Amount", render: (v) => v != null ? `$${Number(v).toFixed(2)}` : "—" },
        { key: "payment_status", label: "Status", render: (v) => (
            <span className={v === "paid" ? "text-[#D4AF37]" : "text-white/60"}>{v || "—"}</span>
          )
        },
        { key: "session_id", label: "Session", render: (v) => v ? <code className="text-[10px] text-white/50">{v.slice(0, 20)}…</code> : "—" },
        { key: "_notify", label: "Email Update", render: (_v, row) => <OrderNotifyCell row={row} /> },
        date,
      ];
    case "referrals":
      return [
        { key: "code", label: "Code", render: (v) => <code className="text-[#D4AF37]">{v}</code> },
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "total_clicks", label: "Clicks" },
        { key: "total_referrals", label: "Started" },
        { key: "total_paid_referrals", label: "Paid" },
        { key: "credit_balance_usd", label: "Credit", render: (v) => v != null ? `$${Number(v).toFixed(0)}` : "$0" },
        { key: "free_sovereign_earned", label: "Free Sovereign", render: (v) => v ? <span className="text-[#D4AF37]">Yes</span> : "—" },
        date,
      ];
    case "referral-events":
      return [
        { key: "code", label: "Code", render: (v) => <code className="text-[#D4AF37]">{v}</code> },
        { key: "referred_email", label: "Referred Email" },
        { key: "package_id", label: "Package" },
        { key: "amount", label: "Amount", render: (v) => v != null ? `$${Number(v).toFixed(2)}` : "—" },
        { key: "status", label: "Status", render: (v) => (
            <span className={v === "paid" ? "text-[#D4AF37]" : "text-white/60"}>{v || "—"}</span>
          )
        },
        { key: "rewarded", label: "Rewarded", render: (v) => v ? <span className="text-[#D4AF37]">Yes</span> : "—" },
        date,
      ];
    case "analytics-events":
      return [
        { key: "event", label: "Event" },
        { key: "source", label: "Source" },
        { key: "meta", label: "Meta" },
        date,
      ];
    default:
      return Object.keys(sample || {}).slice(0, 6).map((k) => ({ key: k, label: k }));
  }
}

/** Studio-triggered lifecycle email cell for paid orders. */
function OrderNotifyCell({ row }) {
  const [open, setOpen] = useState(false);
  const [template, setTemplate] = useState(ORDER_NOTIFY_TEMPLATES[0].id);
  const [roundNo, setRoundNo] = useState("1");
  const [tracking, setTracking] = useState("");
  const [sending, setSending] = useState(false);

  if (row.payment_status !== "paid") {
    return <span className="text-white/25 text-xs">—</span>;
  }
  if (!row.email) {
    return <span className="text-white/25 text-xs" title="No customer email on file">no email</span>;
  }

  const send = async () => {
    setSending(true);
    try {
      const body = { template };
      if (template === "revision_update") body.round_no = parseInt(roundNo || "1", 10);
      if (template === "order_shipped") body.tracking = tracking;
      const res = await adminPost(`orders/${row.session_id}/notify`, body);
      toast.success(res.status === "sent" ? "Email sent to customer." : `Email status: ${res.status}`);
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`order-notify-open-${row.session_id?.slice(-6)}`}
        className="text-[10px] uppercase tracking-[0.18em] border border-white/15 px-3 py-1.5 text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors whitespace-nowrap"
      >
        Send Update
      </button>
    );
  }

  return (
    <div className="space-y-2 min-w-[200px]" data-testid={`order-notify-panel-${row.session_id?.slice(-6)}`}>
      <select
        value={template}
        onChange={(e) => setTemplate(e.target.value)}
        data-testid="order-notify-template"
        className="w-full bg-[#111111] border border-white/15 text-white/85 text-xs px-2 py-2"
      >
        {ORDER_NOTIFY_TEMPLATES.map((t) => (
          <option key={t.id} value={t.id}>{t.label}</option>
        ))}
      </select>
      {template === "revision_update" && (
        <input
          type="number" min="1" max="9" value={roundNo}
          onChange={(e) => setRoundNo(e.target.value)}
          data-testid="order-notify-round"
          className="w-full bg-[#111111] border border-white/15 text-white/85 text-xs px-2 py-2"
          placeholder="Round #"
        />
      )}
      {template === "order_shipped" && (
        <input
          type="text" value={tracking}
          onChange={(e) => setTracking(e.target.value)}
          data-testid="order-notify-tracking"
          className="w-full bg-[#111111] border border-white/15 text-white/85 text-xs px-2 py-2"
          placeholder="Tracking number (optional)"
        />
      )}
      <div className="flex gap-2">
        <button
          type="button" onClick={send} disabled={sending}
          data-testid="order-notify-send"
          className="flex-1 text-[10px] uppercase tracking-[0.18em] bg-[#D4AF37] text-black px-3 py-2 font-semibold hover:bg-[#e6c245] transition-colors disabled:opacity-50"
        >
          {sending ? "Sending…" : "Send"}
        </button>
        <button
          type="button" onClick={() => setOpen(false)}
          className="text-[10px] uppercase tracking-[0.18em] border border-white/15 px-3 py-2 text-white/60 hover:text-white"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
